// The Rhythm Lab's bell: a salsa cowbell (the bongo player's campana) made by synthesis alone.
//
// A stroke is a sum of inharmonic partials at f0 x ratio. Each partial is a decaying sine with its own gain and decay time,
// plus a little of its own third harmonic for metallic grit. A 4 ms stick click (noise, high-passed near 3 kHz) rides on the front.
// The sum is soft-clipped with tanh, band-limited to 350 Hz - 7 kHz and scaled so the OPEN stroke peaks at `peak`. The attack is
// under 1 ms, so the stroke is a clank and not a beep. Every number is in a voice's table below, to be tuned by ear.
//
// A voice has two strokes: OPEN (the open mouth, on counts 1 3 5 7) and NECK (the damped closed end, between them). The neck stroke
// is the same model at f0 x neck.pitch, with the gains times neck.gain and the decays times neck.decay, then `neckMix` for its place in
// the loop. All functions are pure and deterministic (the click's noise is a fixed hash, not Math.random).

import { butterworth, hashNoise } from './voicedsp.js';

export type BellVoice = 'bongo' | 'mambo' | 'chacha';
export type BellStroke = 'open' | 'neck';

export const BELL_VOICE_NAMES: readonly BellVoice[] = ['bongo', 'mambo', 'chacha'];

export interface BellVoiceSpec {
  /** For the interface: "Bongo", "Mambo", "Cha-cha". */
  readonly label: string;
  /** Pitch of the first partial of the open stroke, Hz. */
  readonly f0: number;
  /** Partial frequencies as multiples of the stroke's f0. */
  readonly ratios: readonly number[];
  /** Level of each partial of the open stroke, relative to the first. */
  readonly gains: readonly number[];
  /** Decay time constant of each partial of the open stroke, seconds (amplitude falls to 1/e in this time). */
  readonly decays: readonly number[];
  /** Level of each partial's third harmonic, as a fraction of the partial's own level. */
  readonly grit: number;
  /** Rise time of every partial, milliseconds (under 1). */
  readonly attackMs: number;
  /** Stick click: length in milliseconds, high-pass corner in Hz, and level relative to the sum of the partial gains (open stroke). */
  readonly click: { readonly ms: number; readonly hz: number; readonly level: number };
  /** Gain into the soft clip, relative to the largest the partials could sum to. About 1 is a light grit; more is harder. */
  readonly drive: number;
  /** Pass band after the soft clip, Hz. */
  readonly band: readonly [number, number];
  /** Peak of the open stroke after band-limiting. Under 0.9. */
  readonly peak: number;
  /** The neck stroke, against the open one: f0 times `pitch`, gains times `gain` (the click too), decays times `decay`. */
  readonly neck: { readonly pitch: number; readonly gain: number; readonly decay: number };
  /** Level of the neck stroke in the loop as a share of what the model gives it (about 45% of the open stroke's level in all). */
  readonly neckMix: number;
  /** Loudness of the open stroke in the lab mix (the bell's `levels` default). */
  readonly level: number;
}

const RATIOS = [1, 1.48, 2.24, 2.97, 3.89, 5.12] as const;
const BONGO_GAINS = [1, 0.8, 0.55, 0.4, 0.25, 0.15] as const;
const BONGO_DECAYS = [0.22, 0.16, 0.1, 0.07, 0.05, 0.035] as const;

/** What the three voices share. */
const COMMON = {
  ratios: RATIOS,
  grit: 0.25,
  attackMs: 0.5,
  click: { ms: 4, hz: 3000, level: 0.12 },
  drive: 1.0,
  band: [350, 7000] as const,
  peak: 0.85,
  neck: { pitch: 1.26, gain: 0.5, decay: 0.35 },
  neckMix: 0.9,
} as const;

/** The voice table: the numbers to tune by ear. Bongo bell is the default. */
export const BELL_VOICES: Readonly<Record<BellVoice, BellVoiceSpec>> = {
  bongo: { ...COMMON, label: 'Bongo', f0: 540, gains: BONGO_GAINS, decays: BONGO_DECAYS, level: 0.5 },
  mambo: {
    ...COMMON, label: 'Mambo', f0: 700, gains: [1, 0.9, 0.75, 0.6, 0.4, 0.3], decays: BONGO_DECAYS.map((d) => d * 1.2), level: 0.5,
  },
  chacha: { ...COMMON, label: 'Cha-cha', f0: 980, gains: BONGO_GAINS, decays: BONGO_DECAYS.map((d) => d * 0.6), level: 0.5 },
};

export const DEFAULT_BELL: BellVoice = 'bongo';

/** A stroke runs this many decay constants (of its slowest partial): e^-8 is -69 dB, where it is cut. */
const TAIL = 8;

/** Seconds a stroke lasts in the buffer, for the slowest partial. */
export function bellStrokeSeconds(voice: BellVoice, stroke: BellStroke): number {
  const v = BELL_VOICES[voice];
  return TAIL * Math.max(...v.decays) * (stroke === 'neck' ? v.neck.decay : 1);
}

/** The stroke before the soft clip, band-limit and scaling: partials, grit and click, in units where `drive` 1 is the largest the partials could sum to. */
function rawStroke(voice: BellVoice, stroke: BellStroke, sampleRate: number): Float64Array {
  const v = BELL_VOICES[voice];
  const neck = stroke === 'neck';
  const f0 = v.f0 * (neck ? v.neck.pitch : 1);
  const gainK = neck ? v.neck.gain : 1;
  const decayK = neck ? v.neck.decay : 1;
  const n = Math.ceil(bellStrokeSeconds(voice, stroke) * sampleRate);
  const out = new Float64Array(n);
  const ceiling = Math.min(1.3 * v.band[1], 0.45 * sampleRate); // a component above this is out of the band or would alias
  const attack = Math.max(1, Math.round((v.attackMs / 1000) * sampleRate));
  const ref = v.gains.reduce((s, g) => s + g * (1 + v.grit), 0);

  v.ratios.forEach((ratio, i) => {
    const tau = v.decays[i]! * decayK * sampleRate; // samples
    const decay = Math.exp(-1 / tau);
    for (const [harmonic, level] of [[1, 1], [3, v.grit]] as const) {
      const hz = f0 * ratio * harmonic;
      if (hz > ceiling) continue;
      const amp = (v.drive * v.gains[i]! * gainK * level) / ref;
      const w = (2 * Math.PI * hz) / sampleRate;
      const a = decay * Math.cos(w), b = decay * Math.sin(w);
      let re = 1, im = 0; // z_k = decay^k e^(i w k); its imaginary part is the damped sine
      const last = Math.min(n, Math.ceil(TAIL * tau));
      for (let k = 0; k < last; k++) {
        out[k]! += amp * im * (k < attack ? k / attack : 1);
        const t = re * a - im * b;
        im = re * b + im * a;
        re = t;
      }
    }
  });

  // the stick click: noise, high-passed near its corner, over a few ms with a fast fall
  const clickN = Math.min(n, Math.round((v.click.ms / 1000) * sampleRate));
  const click = new Float64Array(clickN);
  const hp = Math.exp((-2 * Math.PI * v.click.hz) / sampleRate); // one-pole low-pass, subtracted: a high-pass
  let low = 0;
  for (let k = 0; k < clickN; k++) {
    const x = hashNoise(k);
    low = hp * low + (1 - hp) * x;
    const fall = Math.exp(-k / (0.0008 * sampleRate)) * Math.min(1, (k + 1) / attack);
    click[k] = (x - low) * fall;
  }
  const clickAmp = (v.drive * v.click.level * gainK * v.gains.reduce((s, g) => s + g, 0)) / ref;
  for (let k = 0; k < clickN; k++) out[k]! += clickAmp * click[k]!;
  return out;
}

/** Soft-clip and band-limit a raw stroke, in place. */
function finish(raw: Float64Array, v: BellVoiceSpec, sampleRate: number): Float64Array {
  for (let i = 0; i < raw.length; i++) raw[i] = Math.tanh(raw[i]!);
  butterworth(raw, 'high', v.band[0], sampleRate);
  butterworth(raw, 'low', v.band[1], sampleRate);
  return raw;
}

/**
 * Both strokes of a voice at a sample rate, as played in the loop: the open stroke peaks at the voice's `peak`, and the neck stroke is
 * scaled by the same factor and by `neckMix`. Pure.
 */
export function bellStrokes(voice: BellVoice, sampleRate: number): Readonly<Record<BellStroke, Float32Array>> {
  const v = BELL_VOICES[voice];
  const open = finish(rawStroke(voice, 'open', sampleRate), v, sampleRate);
  const neck = finish(rawStroke(voice, 'neck', sampleRate), v, sampleRate);
  let peak = 0;
  for (const x of open) peak = Math.max(peak, Math.abs(x));
  const k = peak > 0 ? v.peak / peak : 0;
  return {
    open: Float32Array.from(open, (x) => x * k),
    neck: Float32Array.from(neck, (x) => x * k * v.neckMix),
  };
}

/** One stroke of a voice, as `bellStrokes` plays it. */
export function bellStroke(voice: BellVoice, stroke: BellStroke, sampleRate: number): Float32Array {
  return bellStrokes(voice, sampleRate)[stroke];
}
